import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-pagination-table-footer',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './pagination-table-footer.component.html',
  styleUrls: ['./pagination-table-footer.component.scss'],
})
export class PaginationTableFooterComponent implements OnChanges {
  @Input() currentPage = 1;
  @Input() pageSize = 50;
  @Input() pageSizeOptions: number[] = [5, 10, 25, 50, 100];
  @Input() totalItems = 0;
  @Input() totalPages = 0;
  @Input() itemCount = 0;
  @Input() serialNumberArray: number[] = [];
  @Input() inputPageValue = 1;

  @Output() currentPageChange = new EventEmitter<number>();
  @Output() pageSizeChange = new EventEmitter<number>();
  @Output() inputPageValueChange = new EventEmitter<number>();
  @Output() pageChange = new EventEmitter<number>();

  pageInputValue = 1;
  private readonly visiblePageDelta = 1;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['inputPageValue'] || changes['currentPage']) {
      this.pageInputValue = Number(this.inputPageValue || this.currentPage || 1);
    }
  }

  get startItem(): number {
    if (this.serialNumberArray.length > 0) {
      return this.serialNumberArray[0] || 0;
    }

    return this.totalItems === 0 ? 0 : (this.currentPage - 1) * this.pageSize + 1;
  }

  get endItem(): number {
    if (this.serialNumberArray.length > 0) {
      return this.serialNumberArray[this.serialNumberArray.length - 1] || 0;
    }

    return Math.min(this.currentPage * this.pageSize, this.totalItems);
  }

  get effectiveTotalPages(): number {
    return this.totalPages || Math.ceil(this.totalItems / this.pageSize);
  }

  get visiblePages(): (number | string)[] {
    const total = this.effectiveTotalPages;
    const current = this.currentPage;
    const delta = this.visiblePageDelta;
    const pages: (number | string)[] = [];

    for (let i = 1; i <= total; i++) {
      if (
        i === 1 ||
        i === total ||
        (i >= current - delta && i <= current + delta)
      ) {
        pages.push(i);
      } else if (pages[pages.length - 1] !== '...') {
        pages.push('...');
      }
    }

    return pages;
  }

  getPageItemClasses(item: number | string, index: number): Record<string, boolean> {
    const pages = this.visiblePages;
    const previousNumericPage = [...pages]
      .slice(0, index)
      .reverse()
      .find((page): page is number => typeof page === 'number');
    const nextNumericPage = pages
      .slice(index + 1)
      .find((page): page is number => typeof page === 'number');

    return {
      active: item === this.currentPage,
      'page-delta-minus-one':
        typeof item === 'number' &&
        item < this.currentPage &&
        nextNumericPage === this.currentPage,
      'page-delta-plus-two':
        typeof item === 'number' &&
        item > this.currentPage &&
        previousNumericPage === this.currentPage,
    };
  }

  changePageSize(newPageSize: number | string): void {
    const nextPageSize = Number(newPageSize);
    if (Number.isNaN(nextPageSize) || nextPageSize <= 0) {
      return;
    }

    this.pageSize = nextPageSize;
    this.currentPage = 1;
    this.pageInputValue = 1;

    this.pageSizeChange.emit(nextPageSize);
    this.currentPageChange.emit(this.currentPage);
    this.inputPageValueChange.emit(this.pageInputValue);
  }

  moveToPage(page: number | string): void {
    const targetPage = Number(page);
    if (
      Number.isNaN(targetPage) ||
      targetPage < 1 ||
      targetPage > this.effectiveTotalPages
    ) {
      this.pageInputValue = this.currentPage;
      this.inputPageValueChange.emit(this.pageInputValue);
      return;
    }

    this.currentPage = targetPage;
    this.pageInputValue = targetPage;

    this.currentPageChange.emit(targetPage);
    this.inputPageValueChange.emit(targetPage);
    this.pageChange.emit(targetPage);
  }

  inputPage(value: number | string): void {
    this.moveToPage(value);
  }
}
